import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';
import { prefersReducedMotion } from '@theme/utilities';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [zoomDialogComponent] - The zoom dialog host component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  /** @type {IntersectionObserver | undefined} */
  #metafieldVideoObserver;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#initMetafieldVideoAutoplay());
    });
  }

  #controller = new AbortController();

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#metafieldVideoObserver?.disconnect();
    this.#metafieldVideoObserver = undefined;

    this.#controller.abort();
  }

  /**
   * External CDN `custom.product_video` slides (when Liquid appends the metafield video slide): load + play when mostly in view (carousel or grid),
   * pause when off-screen. Relies on `<video autoplay muted>` for browser autoplay policy.
   */
  #initMetafieldVideoAutoplay() {
    if (!this.isConnected) return;

    const elements = this.querySelectorAll('deferred-media[data-autoplay-when-selected]');
    if (!elements.length) return;

    this.#metafieldVideoObserver?.disconnect();

    const slideshow = this.refs.slideshow;
    const scroller = slideshow?.refs?.scroller;
    let root = null;
    if (
      slideshow instanceof HTMLElement &&
      scroller instanceof HTMLElement &&
      getComputedStyle(slideshow).display !== 'none'
    ) {
      root = scroller;
    }

    this.#metafieldVideoObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const dm = entry.target;
          if (!(dm instanceof HTMLElement)) continue;

          if (!entry.isIntersecting) {
            dm.pauseMedia?.();
            continue;
          }

          if (prefersReducedMotion()) continue;

          if (entry.intersectionRatio < 0.35) continue;

          if (dm.getAttribute('data-media-loaded')) {
            dm.playMedia?.();
          } else {
            dm.showDeferredMedia?.();
          }
        }
      },
      { root, rootMargin: '0px', threshold: [0, 0.15, 0.25, 0.35, 0.5, 0.65, 1] }
    );

    for (const el of elements) {
      this.#metafieldVideoObserver.observe(el);
    }
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
